import { TZDate, TZDateMini } from '@date-fns/tz';
import { addSeconds, endOfDay, isBefore, startOfDay } from 'date-fns';

import { parseTimeAsTimeValue } from '@dynatrace-sdk/units';
import {
  getTimezone,
  getRegionalFormat,
} from '@dynatrace-sdk/user-preferences';

import { applyInitialTime, getSecondFractions } from './date-time.js';
import type { StringTimeOffset } from './types.js';
import {
  _isSimpleTimeOffset,
  type TimeValue,
  type TimeframeV2,
} from '../../../core/types/time.js';
import { _isOutOfBounds } from '../../calendar/state/calendar-validation.js';
import { CalendarState } from '../../calendar/types/shared-types.js';
import { removePrecision } from '../../date-time-range-picker/helpers.js';

/**
 *  Sanitizes TimeframeV2 to have a from date that comes before the to date and ensures the times are applied properly.
 * @internal
 */
export function _sortTimeframeV2Values(
  value: Partial<TimeframeV2>,
  min?: string,
  max?: string,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
) {
  if (
    value?.from?.absoluteDate &&
    value?.to?.absoluteDate &&
    isBefore(value.to.absoluteDate, value.from.absoluteDate)
  ) {
    // Swaps the `from` and the `to`, to ensure `from` is before `to`.
    // Since the times should be kept on the position, the time value must be reevaluated with the new time offsets.
    return {
      from: _getTimeValueFromSimpleValue(
        value.to.absoluteDate,
        value.from.absoluteDate,
        'from',
        value.from.type === 'expression',
        min,
        max,
        precision,
      ),
      to: _getTimeValueFromSimpleValue(
        value.from.absoluteDate,
        value.to.absoluteDate,
        'to',
        value.to.type === 'expression',
        min,
        max,
        precision,
      ),
    };
  }

  return {
    from: value?.from,
    to: value?.to,
  };
}

/**
 * Gets the dayPeriod values such as AM/PM, a.m./p.m. etc. depending on the user's region.
 * @internal
 */
export const getDayPeriodValues = () =>
  new Intl.DisplayNames(getRegionalFormat(), {
    type: 'dateTimeField',
  })
    .of('dayPeriod')
    ?.split('/') ?? ['AM', 'PM'];

/**
 * Converts a given Date or ISO string to the TimeValue structure for the calendar value.
 * @internal
 */
export const _getTimeValueFromSimpleValue = (
  candidate?: string | Date | null,
  timeOffset?: StringTimeOffset,
  position?: CalendarState['position'],
  isExpression?: boolean,
  min?: string,
  max?: string,
  precision?: 'minutes' | 'seconds' | 'milliseconds',
): TimeValue | undefined => {
  if (!candidate) {
    return undefined;
  }

  const stringDate =
    typeof candidate === 'string' ? candidate : candidate.toISOString();
  const timezone = getTimezone();
  const date = new TZDateMini(stringDate, timezone);

  let newTimeOffset = timeOffset;

  const offsetByPrecision = { milliseconds: 0, seconds: 1, minutes: 60 };
  const getOffsetSeconds = (minDate: TZDate, min: string, precision?: string) =>
    // Only add the offset when the min has different precision than the calendar.
    minDate.getTime() === new TZDateMini(min, timezone).getTime()
      ? 0
      : offsetByPrecision[precision as keyof typeof offsetByPrecision] ?? 0;

  // Enforce min boundary if candidate is before min and position is not 'to'.
  if (min && position !== 'to' && _isOutOfBounds(startOfDay(date), min)) {
    const minDate = new TZDateMini(removePrecision(min, precision), timezone);
    const offsetSeconds = getOffsetSeconds(minDate, min, precision);
    // Add one minute/second depending on the precision to keep the timeValue larger than the min.
    const minOffset = addSeconds(minDate, offsetSeconds).toISOString();

    newTimeOffset =
      typeof newTimeOffset !== 'string' && newTimeOffset
        ? { from: minOffset, to: newTimeOffset.to }
        : minOffset;
  }

  // Enforce max boundary if candidate is after max and position is not 'from'.
  if (
    max &&
    position !== 'from' &&
    _isOutOfBounds(endOfDay(date), undefined, max)
  ) {
    newTimeOffset =
      typeof newTimeOffset !== 'string' && newTimeOffset
        ? { from: newTimeOffset.from, to: max }
        : max;
  }

  const parsedDate = parseTimeAsTimeValue(stringDate);
  if (!parsedDate) {
    return undefined;
  }

  const isRelativeExpression = isExpression || parsedDate.type === 'expression';
  const dateWithTime = _getPreciseIsoString(
    parsedDate.absoluteDate,
    newTimeOffset,
    position,
    isRelativeExpression,
  );

  if (isRelativeExpression === true) {
    return {
      ...parsedDate,
      absoluteDate: dateWithTime,
      type: 'expression',
    };
  }

  return { ...parsedDate, absoluteDate: dateWithTime, value: dateWithTime };
};

/** Applies the proper timeoffset and returns the date as ISO String. */
function _getPreciseIsoString(
  isoString: string,
  timeOffset?: StringTimeOffset,
  position?: CalendarState['position'],
  isExpression?: boolean,
) {
  if (!timeOffset) {
    return isoString;
  }

  const offsetDate = _isSimpleTimeOffset(timeOffset)
    ? timeOffset
    : timeOffset[position ?? 'from'];

  const date = new TZDateMini(isoString, getTimezone());

  if (isExpression) {
    return date.toISOString();
  }

  switch (offsetDate) {
    case 'startOfDay':
      return startOfDay(date).toISOString();
    case 'endOfDay':
      return endOfDay(date).toISOString();
    default: {
      if (isoString === offsetDate) {
        return isoString;
      }

      const precision = getSecondFractions(offsetDate);
      return applyInitialTime(isoString, offsetDate, precision);
    }
  }
}
